import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, User, Scale, Ruler, Phone, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function PublicRegistration() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_first_name: '',
    child_last_name: '',
    child_dob: '',
    child_gender: '',
    current_height: '',
    current_weight: '',
    test_date: '',
    additional_notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        child_dob: new Date(formData.child_dob).toISOString(),
        test_date: formData.test_date ? new Date(formData.test_date).toISOString() : null,
        current_height: parseFloat(formData.current_height),
        current_weight: parseFloat(formData.current_weight)
      };

      await axios.post(`${API}/register`, payload);
      setRegistered(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit registration');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (registered) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="bg-zinc-900 rounded-3xl shadow-2xl p-12 max-w-2xl w-full text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-[#0099dd] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-white" />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Registration Complete!</h2>
          <p className="text-lg text-gray-400 mb-8 leading-relaxed">
            Thank you for registering with BE ELITE APP. We've received your information and will be in touch shortly to confirm your appointment.
          </p>
          <div className="bg-[#02b5ff]/10 rounded-2xl p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-3">What's Next?</h3>
            <ul className="text-left text-gray-300 space-y-2">
              <li className="flex items-start">
                <span className="text-[#02b5ff] mr-2">✓</span>
                <span>Check your email for confirmation details</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#02b5ff] mr-2">✓</span>
                <span>We'll contact you to finalize the appointment time</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#02b5ff] mr-2">✓</span>
                <span>Receive your personalized assessment report</span>
              </li>
            </ul>
          </div>
          <Button
            onClick={() => window.location.href = 'https://www.eliteplayerperformance.co.uk'}
            className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold rounded-full shadow-lg"
          >
            Back to Website
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-zinc-950 shadow-sm border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            BE ELITE APP
          </h1>
          <p className="text-lg text-gray-400">Register for Growth & Performance Assessment</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((num) => (
              <div key={num} className="flex items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                    step >= num
                      ? 'bg-gradient-to-br from-cyan-500 to-cyan-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  } transition-all`}
                >
                  {num}
                </div>
                {num < 3 && (
                  <div
                    className={`w-16 h-1 mx-2 ${
                      step > num ? 'bg-[#02b5ff]' : 'bg-gray-200'
                    } transition-all`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-4 space-x-20">
            <span className={`text-sm font-medium ${step >= 1 ? 'text-[#02b5ff]' : 'text-gray-500'}`}>
              Parent Info
            </span>
            <span className={`text-sm font-medium ${step >= 2 ? 'text-[#02b5ff]' : 'text-gray-500'}`}>
              Child Info
            </span>
            <span className={`text-sm font-medium ${step >= 3 ? 'text-[#02b5ff]' : 'text-gray-500'}`}>
              Book Session
            </span>
          </div>
        </div>

        <div className="bg-zinc-900 rounded-3xl shadow-xl p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Step 1: Parent Information */}
            {step === 1 && (
              <div className="space-y-6" data-testid="step-parent-info">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Parent/Guardian Information</h2>
                  <p className="text-gray-400">We'll use this to contact you about your appointment</p>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    <User className="inline h-4 w-4 mr-1" />
                    Your Name *
                  </Label>
                  <Input
                    data-testid="parent-name-input"
                    required
                    value={formData.parent_name}
                    onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      <Mail className="inline h-4 w-4 mr-1" />
                      Email Address *
                    </Label>
                    <Input
                      data-testid="parent-email-input"
                      type="email"
                      required
                      value={formData.parent_email}
                      onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                      className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                      placeholder="john.smith@email.com"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      <Phone className="inline h-4 w-4 mr-1" />
                      Phone Number
                    </Label>
                    <Input
                      data-testid="parent-phone-input"
                      type="tel"
                      value={formData.parent_phone}
                      onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })}
                      className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                      placeholder="07XXX XXXXXX"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    data-testid="next-button-step1"
                    type="button"
                    onClick={() => {
                      if (!formData.parent_name || !formData.parent_email) {
                        toast.error('Please fill in all required fields');
                        return;
                      }
                      setStep(2);
                    }}
                    className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold rounded-full shadow-lg"
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Child Information */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-child-info">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Child's Information</h2>
                  <p className="text-gray-400">Tell us about the young athlete</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      First Name *
                    </Label>
                    <Input
                      data-testid="child-first-name-input"
                      required
                      value={formData.child_first_name}
                      onChange={(e) => setFormData({ ...formData, child_first_name: e.target.value })}
                      className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      Last Name *
                    </Label>
                    <Input
                      data-testid="child-last-name-input"
                      required
                      value={formData.child_last_name}
                      onChange={(e) => setFormData({ ...formData, child_last_name: e.target.value })}
                      className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      <Calendar className="inline h-4 w-4 mr-1" />
                      Date of Birth *
                    </Label>
                    <Input
                      data-testid="child-dob-input"
                      type="date"
                      required
                      value={formData.child_dob}
                      onChange={(e) => setFormData({ ...formData, child_dob: e.target.value })}
                      className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      Gender *
                    </Label>
                    <Select
                      required
                      value={formData.child_gender}
                      onValueChange={(value) => setFormData({ ...formData, child_gender: value })}
                    >
                      <SelectTrigger data-testid="child-gender-select" className="h-12 bg-zinc-800 border-zinc-700 text-white rounded-xl">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-zinc-700 rounded-xl">
                        <SelectItem value="male" className="rounded-lg">Male</SelectItem>
                        <SelectItem value="female" className="rounded-lg">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      <Ruler className="inline h-4 w-4 mr-1" />
                      Current Height (cm) *
                    </Label>
                    <Input
                      data-testid="child-height-input"
                      type="number"
                      step="0.1"
                      required
                      value={formData.current_height}
                      onChange={(e) => setFormData({ ...formData, current_height: e.target.value })}
                      className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                      placeholder="120.5"
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                      <Scale className="inline h-4 w-4 mr-1" />
                      Current Weight (kg) *
                    </Label>
                    <Input
                      data-testid="child-weight-input"
                      type="number"
                      step="0.1"
                      required
                      value={formData.current_weight}
                      onChange={(e) => setFormData({ ...formData, current_weight: e.target.value })}
                      className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                      placeholder="25.5"
                    />
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    data-testid="back-button-step2"
                    type="button"
                    onClick={() => setStep(1)}
                    className="h-12 px-8 bg-gray-100 hover:bg-gray-200 text-gray-300 font-semibold rounded-full"
                  >
                    Back
                  </Button>
                  <Button
                    data-testid="next-button-step2"
                    type="button"
                    onClick={() => {
                      if (!formData.child_first_name || !formData.child_last_name || !formData.child_dob || 
                          !formData.child_gender || !formData.current_height || !formData.current_weight) {
                        toast.error('Please fill in all required fields');
                        return;
                      }
                      setStep(3);
                    }}
                    className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold rounded-full shadow-lg flex-1"
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Book Session */}
            {step === 3 && (
              <div className="space-y-6" data-testid="step-book-session">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Book Your Assessment Session</h2>
                  <p className="text-gray-400">Choose a preferred date or skip to book later</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 mb-6">
                  <h3 className="text-xl font-bold text-white mb-4">Assessment Includes:</h3>
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-[#02b5ff] mr-3 text-xl">✓</span>
                      <span>Complete growth measurements and analysis</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#02b5ff] mr-3 text-xl">✓</span>
                      <span>Athletic performance testing</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#02b5ff] mr-3 text-xl">✓</span>
                      <span>Personalized development recommendations</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#02b5ff] mr-3 text-xl">✓</span>
                      <span>Detailed report with growth charts</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Preferred Test Date (Optional)
                  </Label>
                  <Input
                    data-testid="test-date-input"
                    type="date"
                    value={formData.test_date}
                    onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
                    className="h-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-sm text-gray-500 mt-2">We'll confirm availability and finalize the appointment</p>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    data-testid="additional-notes-input"
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    className="min-h-24 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-cyan-500 text-white rounded-xl"
                    placeholder="Any specific concerns, goals, or questions?"
                  />
                </div>

                {/* Setmore Booking Widget */}
                <div className="border-t border-zinc-700 pt-6">
                  <h3 className="text-lg font-bold text-white mb-4">Or Book Directly with Setmore</h3>
                  <div className="bg-zinc-800 rounded-2xl p-6">
                    <p className="text-gray-400 mb-4">Click below to view available times and book your one-to-one coaching session directly:</p>
                    <Button
                      type="button"
                      onClick={() => window.open('https://eliteplayerperformance.setmore.com/classes/f08ffba1-a724-4494-bfee-c941df916ff0', '_blank')}
                      className="h-12 px-8 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-full shadow-lg"
                    >
                      View Available Times on Setmore
                    </Button>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    data-testid="back-button-step3"
                    type="button"
                    onClick={() => setStep(2)}
                    className="h-12 px-8 bg-gray-100 hover:bg-gray-200 text-gray-300 font-semibold rounded-full"
                  >
                    Back
                  </Button>
                  <Button
                    data-testid="submit-registration-button"
                    type="submit"
                    disabled={submitting}
                    className="h-12 px-8 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold rounded-full shadow-lg flex-1"
                  >
                    {submitting ? 'Submitting...' : 'Complete Registration'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}